import NumberInput from "../../../components/NumberInput";
import React, { useEffect, useState } from "react";
import useFormValidation from "../../../hooks/useFormValidation";
import { FieldFeedback, FormValidationNotice, RequiredMark } from "../../../components/FormValidation";
import { validationRules } from "../../../utils/helpers/formValidation";
import ModalShell from "./ModalShell";
import AkasSelect from "../../../components/AkasSelect";

const EMPTY = {
  name: "",
  type: "cash",
  bank_name: "",
  account_number: "",
  account_holder: "",
  opening_balance: "",
  payment_methods: [],
  is_active: true,
};

const METHODS = [
  { value: "cash", label: "Tunai" },
  { value: "transfer", label: "Transfer" },
  { value: "qris", label: "QRIS" },
];

const AccountFormModal = ({ account, saving, onClose, onSave }) => {
  const [form, setForm] = useState(EMPTY);
  const isEdit = Boolean(account?.id);
  const validation = useFormValidation({
    values: form,
    formId: "cash-bank-account-form",
    schema: {
      name: [validationRules.required("Nama akun wajib diisi."), validationRules.maxLength(120)],
      type: [validationRules.required("Jenis akun wajib dipilih."), validationRules.oneOf(["cash", "bank", "e_wallet"])],
      opening_balance: isEdit
        ? []
        : [validationRules.number(), validationRules.integer("Saldo awal harus berupa rupiah bulat."), validationRules.min(0, "Saldo awal tidak boleh negatif.")],
      bank_name: [validationRules.maxLength(100)],
      account_number: [validationRules.maxLength(80)],
      account_holder: [validationRules.maxLength(120)],
    },
  });

  useEffect(() => {
    setForm(account ? {
      ...EMPTY,
      ...account,
      opening_balance: "",
      payment_methods: account.payment_methods || [],
      is_active: account.is_active !== false && Number(account.is_active) !== 0,
    } : EMPTY);
  }, [account]);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const toggleMethod = (method) => update(
    "payment_methods",
    form.payment_methods.includes(method)
      ? form.payment_methods.filter((item) => item !== method)
      : [...form.payment_methods, method],
  );
  const save = (event) => {
    event.preventDefault();
    if (!validation.validateAll()) return;
    onSave({
      name: form.name.trim(),
      type: form.type,
      bank_name: form.bank_name || null,
      account_number: form.account_number || null,
      account_holder: form.account_holder || null,
      payment_methods: form.payment_methods,
      ...(isEdit ? { is_active: Boolean(form.is_active) } : { opening_balance: Number(form.opening_balance || 0) }),
    });
  };

  return (
    <ModalShell
      eyebrow="KONFIGURASI AKUN"
      title={isEdit ? "Edit akun Kas & Bank" : "Tambah akun Kas & Bank"}
      subtitle="Saldo tidak dapat diedit langsung. Koreksi saldo dilakukan melalui opname agar histori tetap utuh."
      onClose={onClose}
      onSubmit={save}
      saving={saving}
      submitText={isEdit ? "Simpan perubahan" : "Tambah akun"}>
      <FormValidationNotice errors={validation.errors} />
      <div className="row g-3">
        <div className="col-md-7">
          <label className="form-label">Nama akun <RequiredMark /></label>
          <input className={validation.inputClass("name")} {...validation.fieldProps("name")} maxLength="120" placeholder="Contoh: Kas Bar, BCA Operasional" value={form.name} onChange={(event) => update("name", event.target.value)} />
          <FieldFeedback formId="cash-bank-account-form" name="name" error={validation.errors.name} hint="Gunakan nama yang mudah dikenali kasir dan owner." />
        </div>
        <div className="col-md-5">
          <label className="form-label">Jenis akun <RequiredMark /></label>
          <AkasSelect className={validation.inputClass("type", "form-select")} {...validation.fieldProps("type")} value={form.type} onChange={(event) => update("type", event.target.value)}>
            <option value="cash">Kas tunai</option>
            <option value="bank">Rekening bank</option>
            <option value="e_wallet">Dompet digital</option>
          </AkasSelect>
        </div>
        {!isEdit ? (
          <div className="col-12">
            <label className="form-label">Saldo awal</label>
            <div className="input-group"><span className="input-group-text">Rp</span><NumberInput className={validation.inputClass("opening_balance")} {...validation.fieldProps("opening_balance")} type="number" min="0" step="1" placeholder="0" value={form.opening_balance} onChange={(event) => update("opening_balance", event.target.value)} /></div>
            <FieldFeedback formId="cash-bank-account-form" name="opening_balance" error={validation.errors.opening_balance} hint="Dicatat sekali sebagai mutasi saldo awal dan tetap terlihat pada histori." />
          </div>
        ) : null}
        {form.type !== "cash" ? <>
          <div className="col-md-5"><label className="form-label">Nama bank / penyedia</label><input className={validation.inputClass("bank_name")} {...validation.fieldProps("bank_name")} maxLength="100" placeholder="BCA, Mandiri, GoPay..." value={form.bank_name || ""} onChange={(event) => update("bank_name", event.target.value)} /><FieldFeedback formId="cash-bank-account-form" name="bank_name" error={validation.errors.bank_name} hint="Contoh: BCA, Mandiri, GoPay." /></div>
          <div className="col-md-7"><label className="form-label">Nomor rekening / akun</label><input className={validation.inputClass("account_number")} {...validation.fieldProps("account_number")} maxLength="80" value={form.account_number || ""} onChange={(event) => update("account_number", event.target.value)} /><FieldFeedback formId="cash-bank-account-form" name="account_number" error={validation.errors.account_number} hint="Rekening yang dipetakan ke Transfer akan ditampilkan kepada kasir di POS." /></div>
          <div className="col-12"><label className="form-label">Nama pemilik akun</label><input className={validation.inputClass("account_holder")} {...validation.fieldProps("account_holder")} maxLength="120" value={form.account_holder || ""} onChange={(event) => update("account_holder", event.target.value)} /><FieldFeedback formId="cash-bank-account-form" name="account_holder" error={validation.errors.account_holder} hint="Nama sesuai rekening atau akun dompet digital." /></div>
        </> : null}
        <div className="col-12">
          <label className="form-label">Terima otomatis dari metode pembayaran</label>
          <div className="cash-bank-method-grid">
            {METHODS.map((method) => (
              <label className={`cash-bank-method-option ${form.payment_methods.includes(method.value) ? "is-active" : ""}`} key={method.value}>
                <input type="checkbox" checked={form.payment_methods.includes(method.value)} onChange={() => toggleMethod(method.value)} />
                <span>{method.label}</span><small>Transaksi {method.label.toLowerCase()} masuk ke akun ini</small>
              </label>
            ))}
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

export default AccountFormModal;
